/**
 * The vault programs (contracts/src): what the game, the settlement bot and the Control Room know about them.
 *
 * - Version 1, `TrenchioEntryEscrow`: the Free and Token rooms' vault (it burns its pots) and the first USDG vault (it
 *   pays the whole pot to the winner).
 * - Version 2, `TrenchioEntryEscrowV2` (oct18a, owner decision: "the betting rooms 5% to another treasury"): the same
 *   vault, but a won pot pays the winner all of it but the vault's fee, `winnerFeeBps` (fixed when the vault is made,
 *   at most 10%), and the fee goes to `feeRecipient` (the owner's own wallet, changeable by the vault's boss) in the same
 *   transaction. A cancelled round is always refunded in full, and burns are unchanged.
 *
 * A vault is recognised by its code on the blockchain: the program's runtime code with its built-in values (the coin,
 * the burn settings, the fee, the signature domain) blanked out, hashed. Pure data and arithmetic: no blockchain here.
 */

/** basis points: hundredths of a percent (500 = 5%) */
export const BASIS_POINTS = 10_000n;
/** the most a vault may take from a won pot (the program refuses more): 10% */
export const MAX_WINNER_FEE_BPS = 1_000;
/** the betting rooms' fee, as the owner set it (October 2026): 5% of each won pot, to the betting treasury */
export const BETTING_FEE_BPS = 500;

export type VaultVersion = 1 | 2;

export interface VaultProgram {
	readonly version: VaultVersion;
	readonly contract: "TrenchioEntryEscrow" | "TrenchioEntryEscrowV2";
	/** keccak256 of the creation code, as compiled for production */
	readonly creationFingerprint: string;
	/** keccak256 of the runtime code with its built-in values zeroed */
	readonly runtimeFingerprint: string;
	/** the runtime code's length in bytes */
	readonly codeLength: number;
	/** where its built-in values (immutables) sit in the runtime code: [start, length] */
	readonly builtInValues: readonly (readonly [number, number])[];
}

/** the programs a vault may run (contracts/artifacts, compiled with the production build profile) */
export const VAULT_PROGRAMS: readonly VaultProgram[] = [
	{
		version: 1,
		contract: "TrenchioEntryEscrow",
		creationFingerprint: "0xad14ff9b8fe51a45e5cb37c53110609f8800349f41ff7e6200fbe7893285e9cd",
		runtimeFingerprint: "0x91971f89386f64ca2af91451e3b38877e3733234ddbad5c126c3ad231779c4dd",
		codeLength: 10_080,
		builtInValues: [
			[857, 32], [1301, 32], [1656, 32], [1813, 32], [1888, 32], [2001, 32], [2035, 32], [2109, 32], [2142, 32], [2837, 32], [3214, 32], [3630, 32],
			[3903, 32], [4930, 32], [4980, 32], [6275, 32], [6419, 32], [6500, 32], [7593, 32], [7635, 32], [7677, 32], [7758, 32], [7798, 32],
		],
	},
	{
		version: 2,
		contract: "TrenchioEntryEscrowV2",
		creationFingerprint: "0x8d6170216d5803213437cb39b89b2f3eb2ee57dd182d31a9efc8014b3bf7ac8f",
		runtimeFingerprint: "0x039d3842f524505e619d8d4425a8e4c1e4ddbf82b95078dfc2100303e8592e23",
		codeLength: 10_838,
		builtInValues: [
			[863, 32], [971, 32], [1434, 32], [1856, 32], [2013, 32], [2088, 32], [2201, 32], [2235, 32], [2309, 32], [2342, 32], [3037, 32], [3451, 32],
			[3585, 32], [3779, 32], [4081, 32], [4354, 32], [4659, 32], [4736, 32], [5634, 32], [5684, 32], [6979, 32], [7123, 32], [7204, 32], [8297, 32],
			[8339, 32], [8381, 32], [8462, 32], [8502, 32],
		],
	},
];

/**
 * Which known program a vault's runtime code is (version 1 or 2), or undefined for anything else: its built-in values
 * blanked, then hashed with `keccak` (viem's keccak256, given by the caller: this module has no dependencies).
 */
export function vaultProgramOf(code: Uint8Array, keccak: (bytes: Uint8Array) => string): VaultProgram | undefined {
	for (const program of VAULT_PROGRAMS) {
		if (code.length !== program.codeLength) continue;
		const bytes = code.slice();
		for (const [start, length] of program.builtInValues) bytes.fill(0, start, start + length);
		if (keccak(bytes).toLowerCase() === program.runtimeFingerprint) return program;
	}
	return undefined;
}

/** a fee rate the vault program accepts: a whole number of basis points from 0 to 10% */
export function validWinnerFeeBps(bps: unknown): bps is number {
	return typeof bps === "number" && Number.isSafeInteger(bps) && bps >= 0 && bps <= MAX_WINNER_FEE_BPS;
}

/**
 * How a won pot splits in a vault taking `feeBps`, exactly as the vault does it: the fee rounded down (never more than
 * the rate), the winner gets the rest. A vault of version 1 takes no fee (0).
 */
export function winnerSplit(potAtomic: bigint, feeBps: number | bigint = 0): { readonly prize: bigint; readonly fee: bigint } {
	const bps = BigInt(feeBps);
	if (potAtomic <= 0n) return { prize: 0n, fee: 0n };
	if (bps <= 0n) return { prize: potAtomic, fee: 0n };
	const fee = (potAtomic * bps) / BASIS_POINTS;
	return { prize: potAtomic - fee, fee };
}

/** a fee rate as people read it: "5%", "2.5%", "0.25%" */
export function feePercentText(feeBps: number): string {
	const whole = Math.trunc(feeBps / 100);
	const rest = feeBps % 100;
	return rest === 0 ? `${whole}%` : `${whole}.${String(rest).padStart(2, "0").replace(/0$/u, "")}%`;
}
